/*                                                                                                                      */
/*                                                      */
import {
	EACH_INDEX_REACTIVE,
	EACH_IS_ANIMATED,
	EACH_IS_CONTROLLED,
	EACH_ITEM_IMMUTABLE,
	EACH_ITEM_REACTIVE,
	HYDRATION_END,
	HYDRATION_START_ELSE
} from '../../../../constants.js';
import {
	hydrate_next,
	hydrate_node,
	hydrating,
	read_hydration_instruction,
	remove_nodes,
	set_hydrate_node,
	set_hydrating
} from '../hydration.js';
import {
	clear_text_content,
	create_text,
	get_first_child,
	get_next_sibling,
	should_defer_append
} from '../operations.js';
import {
	block,
	branch,
	destroy_effect,
	run_out_transitions,
	pause_children,
	pause_effect,
	resume_effect
} from '../../reactivity/effects.js';
import { source, mutable_source, internal_set } from '../../reactivity/sources.js';
import { array_from, is_array } from '../../../shared/utils.js';
import { COMMENT_NODE, INERT } from '#client/constants';
import { queue_micro_task } from '../task.js';
import { active_effect, get } from '../../runtime.js';
import { DEV } from 'esm-env';
import { derived_safe_equal } from '../../reactivity/deriveds.js';
import { current_batch } from '../../reactivity/batch.js';

/**
 *
 *
 *
 */
export let current_each_item = null;

/*                                */
export function set_current_each_item(item) {
	current_each_item = item;
}

/**
 *
 *
 */
export function index(_, i) {
	return i;
}

/**
 *
 *
 *
 *
 *
 */
function pause_effects(state, items, controlled_anchor) {
	var items_map = state.items;

	/*                              */
	var transitions = [];
	var length = items.length;

	for (var i = 0; i < length; i++) {
		pause_children(items[i].e, transitions, true);
	}

	var is_controlled = length > 0 && transitions.length === 0 && controlled_anchor !== null;
	/*                                                                              */
	/*                                                                                 */
	if (is_controlled) {
		var parent_node = /*                  */ (
			/*                  */ (controlled_anchor).parentNode
		);
		clear_text_content(parent_node);
		parent_node.append(/*                  */ (controlled_anchor));
		items_map.clear();
		link(state, items[0].prev, items[length - 1].next);
	}

	run_out_transitions(transitions, () => {
		for (var i = 0; i < length; i++) {
			var item = items[i];
			if (!is_controlled) {
				items_map.delete(item.k);
				link(state, item.prev, item.next);
			}
			destroy_effect(item.e, !is_controlled);
		}
	});
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function each(node, flags, get_collection, get_key, render_fn, fallback_fn = null) {
	var anchor = node;

	/*                    */
	var state = { flags, items: new Map(), first: null };

	var is_controlled = (flags & EACH_IS_CONTROLLED) !== 0;

	if (is_controlled) {
		var parent_node = /*                  */ (node);

		anchor = hydrating
			? set_hydrate_node(/*                         */ (get_first_child(parent_node)))
			: parent_node.appendChild(create_text());
	}

	if (hydrating) {
		hydrate_next();
	}

	/*                        */
	var fallback = null;

	var was_empty = false;

	/*                             */
	var offscreen_items = new Map();

	/*                                                                           */
	/*                                                                             */
	/*                                                                      */
	var each_array = derived_safe_equal(() => {
		var collection = get_collection();

		return is_array(collection) ? collection : collection == null ? [] : array_from(collection);
	});

	/*              */
	var array;

	/*                 */
	var each_effect;

	function commit() {
		reconcile(
			each_effect,
			array,
			state,
			offscreen_items,
			anchor,
			render_fn,
			flags,
			get_key,
			get_collection
		);

		if (fallback_fn !== null) {
			if (array.length === 0) {
				if (fallback) {
					resume_effect(fallback);
				} else {
					fallback = branch(() => fallback_fn(anchor));
				}
			} else if (fallback !== null) {
				pause_effect(fallback, () => {
					fallback = null;
				});
			}
		}
	}

	block(() => {
		/*                                                                                          */
		each_effect ??= /*                 */ (active_effect);

		array = get(each_array);
		var length = array.length;

		if (was_empty && length === 0) {
			/*                                    */
			/*                                       */
			return;
		}
		was_empty = length === 0;

		/*                                                                                                */
		let mismatch = false;

		if (hydrating) {
			var is_else = read_hydration_instruction(anchor) === HYDRATION_START_ELSE;

			if (is_else !== (length === 0)) {
				/*                                                                 */
				anchor = remove_nodes();

				set_hydrate_node(anchor);
				set_hydrating(false);
				mismatch = true;
			}
		}

		/*                                                                      */
		if (hydrating) {
			/*                          */
			var prev = null;

			/*                   */
			var item;

			for (var i = 0; i < length; i++) {
				if (
					hydrate_node.nodeType === COMMENT_NODE &&
					/*                  */ (hydrate_node).data === HYDRATION_END
				) {
					/*                                             */
					/*                                                     */
					anchor = /*                  */ (hydrate_node);
					mismatch = true;
					set_hydrating(false);
					break;
				}

				var value = array[i];
				var key = get_key(value, i);
				item = create_item(
					hydrate_node,
					state,
					prev,
					null,
					value,
					key,
					i,
					render_fn,
					flags,
					get_collection
				);
				state.items.set(key, item);

				prev = item;
			}

			/*                  */
			if (length > 0) {
				set_hydrate_node(remove_nodes());
			}
		}

		if (hydrating) {
			if (length === 0 && fallback_fn) {
				fallback = branch(() => fallback_fn(anchor));
			}
		} else {
			if (should_defer_append()) {
				var keys = new Set();
				var batch = /*                */ (current_batch);

				for (i = 0; i < length; i += 1) {
					value = array[i];
					key = get_key(value, i);

					var existing = state.items.get(key) ?? offscreen_items.get(key);

					if (existing) {
						/*                                                         */
						if ((flags & (EACH_ITEM_REACTIVE | EACH_INDEX_REACTIVE)) !== 0) {
							update_item(existing, value, i, flags);
						}
					} else {
						item = create_item(
							null,
							state,
							null,
							null,
							value,
							key,
							i,
							render_fn,
							flags,
							get_collection,
							true
						);

						offscreen_items.set(key, item);
					}

					keys.add(key);
				}

				for (const [key, item] of state.items) {
					if (!keys.has(key)) {
						batch.skipped_effects.add(item.e);
					}
				}

				batch.add_callback(commit);
			} else {
				commit();
			}
		}

		if (mismatch) {
			/*                         */
			set_hydrating(true);
		}

		/*                                                                       */
		/*                                                                              */
		/*                                                                             */
		/*                                                                                */
		/*                                                                                   */
		/*                   */
		get(each_array);
	});

	if (hydrating) {
		anchor = hydrate_node;
	}
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
function reconcile(
	each_effect,
	array,
	state,
	offscreen_items,
	anchor,
	render_fn,
	flags,
	get_key,
	get_collection
) {
	var is_animated = (flags & EACH_IS_ANIMATED) !== 0;
	var should_update = (flags & (EACH_ITEM_REACTIVE | EACH_INDEX_REACTIVE)) !== 0;

	var length = array.length;
	var items = state.items;
	var first = state.first;
	var current = first;

	/*                                    */
	var seen;

	/*                          */
	var prev = null;

	/*                                    */
	var to_animate;

	/*                     */
	var matched = [];

	/*                     */
	var stashed = [];

	/*            */
	var value;

	/*              */
	var key;

	/*                               */
	var item;

	/*                 */
	var i;

	if (is_animated) {
		for (i = 0; i < length; i += 1) {
			value = array[i];
			key = get_key(value, i);
			item = items.get(key);

			if (item !== undefined) {
				item.a?.measure();
				(to_animate ??= new Set()).add(item);
			}
		}
	}

	for (i = 0; i < length; i += 1) {
		value = array[i];
		key = get_key(value, i);

		item = items.get(key);

		if (item === undefined) {
			var pending = offscreen_items.get(key);

			if (pending !== undefined) {
				offscreen_items.delete(key);
				items.set(key, pending);

				var next = prev ? prev.next : current;

				link(state, prev, pending);
				link(state, pending, next);

				move(pending, next, anchor);
				prev = pending;
			} else {
				var child_anchor = current ? /*                       */ (current.e.nodes_start) : anchor;

				prev = create_item(
					child_anchor,
					state,
					prev,
					prev === null ? state.first : prev.next,
					value,
					key,
					i,
					render_fn,
					flags,
					get_collection
				);
			}

			items.set(key, prev);

			matched = [];
			stashed = [];

			current = prev.next;
			continue;
		}

		if (should_update) {
			update_item(item, value, i, flags);
		}

		if ((item.e.f & INERT) !== 0) {
			resume_effect(item.e);
			if (is_animated) {
				item.a?.unfix();
				(to_animate ??= new Set()).delete(item);
			}
		}

		if (item !== current) {
			if (seen !== undefined && seen.has(item)) {
				if (matched.length < stashed.length) {
					/*                                              */
					var start = stashed[0];
					var j;

					prev = start.prev;

					var a = matched[0];
					var b = matched[matched.length - 1];

					for (j = 0; j < matched.length; j += 1) {
						move(matched[j], start, anchor);
					}

					for (j = 0; j < stashed.length; j += 1) {
						seen.delete(stashed[j]);
					}

					link(state, a.prev, b.next);
					link(state, prev, a);
					link(state, b, start);

					current = start;
					prev = b;
					i -= 1;

					matched = [];
					stashed = [];
				} else {
					/*                                               */
					seen.delete(item);
					move(item, current, anchor);

					link(state, item.prev, item.next);
					link(state, item, prev === null ? state.first : prev.next);
					link(state, prev, item);

					prev = item;
				}

				continue;
			}

			matched = [];
			stashed = [];

			while (current !== null && current.k !== key) {
				/*                                                                             */
				/*                                                                       */
				if ((current.e.f & INERT) === 0) {
					(seen ??= new Set()).add(current);
				}
				stashed.push(current);
				current = current.next;
			}

			if (current === null) {
				continue;
			}

			item = current;
		}

		matched.push(item);
		prev = item;
		current = item.next;
	}

	if (current !== null || seen !== undefined) {
		var to_destroy = seen === undefined ? [] : array_from(seen);

		while (current !== null) {
			/*                                                                                                                           */
			if ((current.e.f & INERT) === 0) {
				to_destroy.push(current);
			}
			current = current.next;
		}

		var destroy_length = to_destroy.length;

		if (destroy_length > 0) {
			var controlled_anchor = (flags & EACH_IS_CONTROLLED) !== 0 && length === 0 ? anchor : null;

			if (is_animated) {
				for (i = 0; i < destroy_length; i += 1) {
					to_destroy[i].a?.measure();
				}

				for (i = 0; i < destroy_length; i += 1) {
					to_destroy[i].a?.fix();
				}
			}

			pause_effects(state, to_destroy, controlled_anchor);
		}
	}

	if (is_animated) {
		queue_micro_task(() => {
			if (to_animate === undefined) return;
			for (item of to_animate) {
				item.a?.apply();
			}
		});
	}

	each_effect.first = state.first && state.first.e;
	each_effect.last = prev && prev.e;

	for (var unused of offscreen_items.values()) {
		destroy_effect(unused.e);
	}

	offscreen_items.clear();
}

/**
 *
 *
 *
 *
 *
 */
function update_item(item, value, index, type) {
	if ((type & EACH_ITEM_REACTIVE) !== 0) {
		internal_set(item.v, value);
	}

	if ((type & EACH_INDEX_REACTIVE) !== 0) {
		internal_set(/*                        */ (item.i), index);
	} else {
		item.i = index;
	}
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
function create_item(
	anchor,
	state,
	prev,
	next,
	value,
	key,
	index,
	render_fn,
	flags,
	get_collection,
	deferred
) {
	var previous_each_item = current_each_item;
	var reactive = (flags & EACH_ITEM_REACTIVE) !== 0;
	var mutable = (flags & EACH_ITEM_IMMUTABLE) === 0;

	var v = reactive ? (mutable ? mutable_source(value, false, false) : source(value)) : value;
	var i = (flags & EACH_INDEX_REACTIVE) === 0 ? index : source(index);

	if (DEV && reactive) {
		/*                                                                         */
		/*                                                   */
		/*                */ (v).trace = () => {
			var collection_index = typeof i === 'number' ? index : i.v;
			/*                                                                */
			get_collection()[collection_index];
		};
	}

	/*                   */
	var item = {
		i,
		v,
		k: key,
		a: null,
		/*               */
		e: null,
		prev,
		next
	};

	current_each_item = item;

	try {
		if (anchor === null) {
			var fragment = document.createDocumentFragment();
			fragment.append((anchor = create_text()));
		}

		item.e = branch(() => render_fn(/*               */ (anchor), v, i, get_collection), hydrating);

		item.e.prev = prev && prev.e;
		item.e.next = next && next.e;

		if (prev === null) {
			if (!deferred) {
				state.first = item;
			}
		} else {
			prev.next = item;
			prev.e.next = item.e;
		}

		if (next !== null) {
			next.prev = item;
			next.e.prev = item.e;
		}

		return item;
	} finally {
		current_each_item = previous_each_item;
	}
}

/**
 *
 *
 *
 */
function move(item, next, anchor) {
	var end = item.next ? /*                       */ (item.next.e.nodes_start) : anchor;

	var dest = next ? /*                       */ (next.e.nodes_start) : anchor;
	var node = /*                       */ (item.e.nodes_start);

	while (node !== null && node !== end) {
		var next_node = /*                       */ (get_next_sibling(node));
		dest.before(node);
		node = next_node;
	}
}

/**
 *
 *
 *
 */
function link(state, prev, next) {
	if (prev === null) {
		state.first = next;
	} else {
		prev.next = next;
		prev.e.next = next && next.e;
	}

	if (next !== null) {
		next.prev = prev;
		next.e.prev = prev && prev.e;
	}
}
